<script lang="ts" setup>
import {RouterLink, useRouter} from 'vue-router'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import {onMounted, ref, computed} from 'vue'
import RollsForm from '@/components/features/rolls/RollsForm.vue'
import {useRollForm} from '@/composables/useRollForm'
import {Icon} from '@iconify/vue'
import StatusTag from '@/components/table/StatusTag.vue'
import Timeline from 'primevue/timeline'
import 'tippy.js/themes/light.css'
import {getRollState} from "@/constants/rolls";
import {getProductList} from "@/api/products";
import type {Product} from "@/types/product-types";
import type {ReturnedBatch} from "@/types/batch-types";
import {getReturnedBatchList} from "@/api/batches";
import { getPackingList } from '@/api/packingList'
import type { PackingList } from '@/types/packingList-types'
import {RollState} from "@/types/roll-types";
import { formatedDate } from '@/constants'
import { useToast } from 'primevue/usetoast'

const router = useRouter()
const toast = useToast()
const { values, loading, handleSubmit, setErrors, showToast, formType, batch } = useRollForm('view')
const visible = ref(false)
const isRollRemoved = ref(false)
const removeRoll = ref(false)
const productsList = ref<Product[]>([])
const returnedBatchList = ref<ReturnedBatch[]>([])
const allPackingLists = ref<PackingList[]>([])

const showTimelineModal = ref(false)
const timelineLoading = ref(false)

const rollId = router.currentRoute.value.params.id as string

const canEditRoll = computed(() => {
  return values?.rollState === RollState.Greig && !isRollRemoved.value
})

const editButtonTooltipContent = computed(() => {
  if (canEditRoll.value) return ''

  if (isRollRemoved.value) {
    return 'Cannot edit removed rolls'
  }

  if (!values?.isActive) {
    return 'Cannot edit inactive rolls'
  }

  if (values?.rollState !== RollState.Greig) {
    return `Cannot edit rolls in ${getRollState(values?.rollState)} status. Only rolls in Greig status can be edited.`
  }

  return 'This roll cannot be edited'
})

const handleEditRoll = () => {
  if (!canEditRoll.value) {
    showToast('warning', 'Cannot Edit Roll', editButtonTooltipContent.value)
    return
  }
  router.push(`/rolls/${rollId}/edit`)
}

const rollTimelineActions = computed(() => {
  const actions = [];

  const cleanDetails = (details: Record<string, any>) => {
    return Object.entries(details)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
      }, {} as Record<string, any>);
  };

  const getRollStateLabel = (state: number) => {
    switch (state) {
      case RollState.Greig: return 'Greige';
      case RollState.Offsite: return 'Offsite';
      case RollState.Finished: return 'Finished';
      case RollState.Available: return 'Available';
      case RollState.Dispatched: return 'Dispatched';
      default: return 'Unknown State';
    }
  };

  const getStateIcon = (state: number) => {
    switch (state) {
      case RollState.Greig: return 'pi pi-circle';
      case RollState.Offsite: return 'pi pi-truck';
      case RollState.Finished: return 'pi pi-check-circle';
      case RollState.Available: return 'pi pi-check';
      case RollState.Dispatched: return 'pi pi-send';
      default: return 'pi pi-question';
    }
  };

  const getStateColor = (state: number) => {
    switch (state) {
      case RollState.Greig: return '#9E9E9E';
      case RollState.Offsite: return '#FF9800';
      case RollState.Finished: return '#4CAF50';
      case RollState.Available: return '#2196F3';
      case RollState.Dispatched: return '#E91E63';
      default: return '#757575';
    }
  };

  if (values?.dateCreated || values?.dateKnitted) {
    const details = {
      'Roll Number': values?.rollNumber || 'Unknown Roll Number',
      'Quality': getQuality(),
      'Batch': getBatchNumber(),
      'Initial Weight': values?.weight ? `${values.weight} kg` : 'Weight not recorded',
      'Initial Length': values?.length ? `${values.length} m` : 'Length not recorded',
      'Initial Status': 'Greige'
    };

    actions.push({
      status: 'Roll Created',
      date: formatedDate(values?.dateCreated || values?.dateKnitted),
      icon: 'pi pi-plus-circle',
      color: '#4CAF50',
      details: cleanDetails(details),
      statusTransition: null
    });
  }

  if (values?.dateGreige && values?.dateGreige !== (values?.dateCreated || values?.dateKnitted)) {
    const details = {
      'Status': 'Greige (Unfinished)',
      'Available for Processing': 'Yes',
      'Quality': getQuality(),
      'Weight': values?.weight ? `${values.weight} kg` : 'Weight not recorded'
    };

    actions.push({
      status: 'Status Changed to Greige',
      date: formatedDate(values.dateGreige),
      icon: getStateIcon(RollState.Greig),
      color: getStateColor(RollState.Greig),
      details: cleanDetails(details),
      statusTransition: `→ ${getRollStateLabel(RollState.Greig)}`
    });
  }

  if (values?.dateSentToSupplier) {
    const details = {
      'Previous Status': values?.previousStatus ? getRollStateLabel(values.previousStatus) : 'Greige',
      'New Status': 'Offsite',
      'Finishing Supplier': values?.supplierName || 'Supplier Name',
      'Quality': getQuality(),
      'Processing Type': values?.finishingQualityId ? 'Finishing Process' : 'Processing',
      'Expected Return': values?.expectedReturnDate ? formatedDate(values.expectedReturnDate) : 'Not specified'
    };

    actions.push({
      status: 'Sent to Supplier',
      date: formatedDate(values.dateSentToSupplier),
      icon: getStateIcon(RollState.Offsite),
      color: getStateColor(RollState.Offsite),
      details: cleanDetails(details),
      statusTransition: `${values?.previousStatus ? getRollStateLabel(values.previousStatus) : 'Greige'} → Offsite`
    });
  }

  if (values?.dateReturned) {
    const details = {
      'Previous Status': 'Offsite',
      'New Status': 'Finished',
      'Final Weight': values?.finalWeight ? `${values.finalWeight} kg` : values?.weight ? `${values.weight} kg` : 'Weight not recorded',
      'Final Length': values?.finalLength ? `${values.finalLength} m` : values?.length ? `${values.length} m` : 'Length not recorded',
      'Quality Check Status': values?.qualityCheckPassed ? 'Passed' : 'Pending',
      'Fire Test Status': values?.fireTestPassed ? 'Passed' : 'Pending'
    };

    actions.push({
      status: 'Returned from Supplier',
      date: formatedDate(values.dateReturned),
      icon: 'pi pi-arrow-left',
      color: '#673AB7',
      details: cleanDetails(details),
      statusTransition: 'Offsite → Finished'
    });
  }

  if (values?.dateAvailable || (values?.rollState === RollState.Available && values?.qualityCheckPassed && values?.fireTestPassed)) {
    const availableDate = values?.dateAvailable || values?.dateQualityApproved || values?.dateReturned;

    const details = {
      'Previous Status': 'Finished',
      'New Status': 'Available',
      'Quality Check': values?.qualityCheckPassed ? 'Passed' : 'Failed',
      'Fire Test': values?.fireTestPassed ? 'Passed' : 'Failed',
      'Customer Packing List': customerPackingList.value.length > 0 ? customerPackingList.value.map(pl => pl.packingListId).join(', ') : 'Not Assigned',
      'Ready for Dispatch': 'Yes'
    };

    actions.push({
      status: 'Available for Dispatch',
      date: formatedDate(availableDate),
      icon: getStateIcon(RollState.Available),
      color: getStateColor(RollState.Available),
      details: cleanDetails(details),
      statusTransition: 'Finished → Available'
    });
  }

  if (values?.dateDispatched) {
    const details = {
      'Previous Status': 'Available',
      'New Status': 'Dispatched',
      'Customer': values?.customerName || 'Customer Name',
      'Dispatch Date': formatedDate(values.dateDispatched),
      'Customer Packing List': customerPackingList.value.map(pl => pl.packingListId).join(', ') || 'Not assigned',
      'Final Weight': values?.finalWeight ? `${values.finalWeight} kg` : 'Weight not recorded',
      'Final Length': values?.finalLength ? `${values.finalLength} m` : 'Length not recorded'
    };

    actions.push({
      status: 'Dispatched to Customer',
      date: formatedDate(values.dateDispatched),
      icon: getStateIcon(RollState.Dispatched),
      color: getStateColor(RollState.Dispatched),
      details: cleanDetails(details),
      statusTransition: 'Available → Dispatched'
    });
  }

  if (!values?.isActive && values?.dateDeactivated) {
    const details = {
      'Previous Status': getRollStateLabel(values?.rollState),
      'New Status': 'Inactive',
      'Reason': values?.deactivationReason || 'Not specified',
      'Deactivated By': values?.deactivatedBy || 'System',
      'Impact': 'Roll removed from active inventory'
    };

    actions.push({
      status: 'Roll Deactivated',
      date: formatedDate(values.dateDeactivated),
      icon: 'pi pi-times-circle',
      color: '#F44336',
      details: cleanDetails(details),
      statusTransition: `${getRollStateLabel(values?.rollState)} → Inactive`
    });
  }

  return actions.sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
});

const openTimelineModal = async () => {
  showTimelineModal.value = true;
  timelineLoading.value = false;
}

const closeTimelineModal = () => {
  showTimelineModal.value = false;
}

const supplierPackingList = computed(() => {
  if (!rollId || allPackingLists.value.length === 0) return []

  return allPackingLists.value.filter(
    (p) => p.packingListType === 0 && (p.rollIds ?? []).includes(rollId)
  )
})

const customerPackingList = computed(() => {
  if (!rollId || allPackingLists.value.length === 0) return []

  return allPackingLists.value.filter(
    (p) => p.packingListType === 1 && (p.rollIds ?? []).includes(rollId)
  )
})

const getQuality = () => {
  const productId = values.finishingQualityId || values.knittingQualityId
  const product = productsList.value.find((product: Product) => product.id === productId)

  if (!product) return 'Unknown Quality'

  return values.finishingQualityId ? product.finishedReference : product.unfinishedReference
}

const getBatchNumber = () => {
  if (!batch.value) return 'Unknown Batch'

  if (batch.value.returnedBatchId) {
    const returnedBatch = returnedBatchList.value.find((rb: any) => rb.id === batch.value.returnedBatchId)
    if (returnedBatch) {
      return returnedBatch.batchNumber || batch.value.batchNumber
    }
  }
  return batch.value.batchNumber
}

onMounted(async () => {
  try {
    ;[productsList.value, returnedBatchList.value, allPackingLists.value] = await Promise.all([
      getProductList(),
      getReturnedBatchList(),
      getPackingList()
    ])
  } catch (error: any) {
    const errorMessages =
      error.response?.data?.errors?.map((err: any) => err.description).join('\n') ||
      'Unable to add a new roll.'
    showToast('error', 'An error occurred!', errorMessages)
  }
})

</script>

<template>
  <div class="bg-white rounded-xl p-10">
    <!-- Main Header -->
    <div class="flex justify-between items-center mb-8">
      <RouterLink to="/rolls" class="flex items-center gap-x-2 group hover:text-primary-default">
        <Icon
          icon="material-symbols:arrow-back-rounded"
          class="text-3xl group-hover:translate-x-2 transition-transform"
        />
        <span>Back</span>
      </RouterLink>

      <h1 class="font-semibold text-2xl">Roll Details</h1>
      <div class="flex items-center gap-x-2 group hover:text-primary-default">
        <div class="flex items-center gap-3">
          <div class="relative inline-block group/tooltip">
            <Button
              @click="handleEditRoll"
              :label="canEditRoll ? 'Edit this Roll' : 'Cannot Edit Roll'"
              class="min-w-48"
              :disabled="!canEditRoll"
              :severity="canEditRoll ? 'primary' : 'secondary'"
              v-show="!isRollRemoved"
              outlined
            >
              <template #icon>
                <div class="flex items-center gap-1">
                  <Icon
                    :icon="canEditRoll ? 'basil:edit-solid' : 'material-symbols:lock'"
                    class="text-xl"
                  />
                  <Icon
                    v-if="!canEditRoll"
                    icon="material-symbols:info"
                    class="text-sm text-yellow-500"
                  />
                </div>
              </template>
            </Button>

            <div
              v-if="!canEditRoll && !isRollRemoved"
              class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10"
            >
              {{ editButtonTooltipContent }}
              <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
            </div>
          </div>

          <Button
            @click="
            () => {
              isRollRemoved = false
              removeRoll = false
            }
          "
            label="Restore Roll"
            class="min-w-48"
            v-show="isRollRemoved"
          >
            <template #icon>
              <Icon icon="material-symbols:restore-from-trash" class="text-xl ml-3 order-1" />
            </template>
          </Button>
        </div>

        <div class="flex items-center gap-3">
          <Button
            @click="openTimelineModal"
            variant="text"
            class="flex items-center gap-2 text-gray-700 hover:bg-primary-200 group"
          >
            <Icon icon="mdi:timeline-clock-outline" class="text-xl" />
            <span>Roll History</span>
            <Icon
              icon="material-symbols:arrow-forward"
              class="text-xl group-hover:translate-x-2 transition-transform"
            />
          </Button>
        </div>
      </div>
    </div>

    <div class="flex justify-between items-center mb-8">
      <div class="flex items-center gap-4">
        <div
          class="bg-primary-300 bg-opacity-25 border border-primary-500 text-primary-500 py-2 px-3 pe-10 rounded-lg"
          :class="
            isRollRemoved
              ? 'border-gray-400 text-gray-500'
              : 'border-orange-500 text-orange-500'
          "
        >
          <span class="text-lg font-medium">Roll Number: {{ values?.rollNumber }}</span>
        </div>
        <StatusTag :status="getRollState(values?.rollState)" v-show="!isRollRemoved" />
        <StatusTag id="reason" status="Removed" v-show="isRollRemoved" />
      </div>
    </div>

    <div class="mb-8">
      <div class="flex items-center gap-5">
        <h3 class="text-lg font-semibold text-gray-800">Quality</h3>
        <RouterLink
          :to="`/productSpecification/${values.finishingQualityId || values.knittingQualityId}`"
          class="flex gap-2 items-center bg-green-100 text-green-600 py-2 px-3 rounded-lg hover:bg-green-200 transition-colors w-fit"
        >
          <Icon icon="mdi:fabric" class="text-xl" />
          {{ getQuality() }}
        </RouterLink>
      </div>
    </div>

    <!-- Legacy Simple History Dialog (kept for backward compatibility) -->
    <Dialog v-model:visible="visible" modal :draggable="false" class="max-w-md" :closable="false">
      <template #header>
        <div class="w-full inline-flex align-items-center justify-between">
          <p class="font-semibold text-base text-slate-900 mb-4">Roll History (Legacy)</p>
          <Icon
            @click="visible = false"
            icon="material-symbols:close"
            class="text-xl group-hover:translate-x-2 transition-transform text-slate-500 cursor-pointer"
          />
        </div>
      </template>
      <div class="text-center text-gray-500 py-4">
        <p>This legacy history view has been replaced.</p>
        <p>Please use the new "Roll History" button for detailed timeline.</p>
      </div>
    </Dialog>

    <!-- Enhanced Timeline Modal -->
    <Dialog
      v-model:visible="showTimelineModal"
      :style="{ width: '70vw', height: '85vh', maxWidth: '1000px' }"
      :maximizable="true"
      :closable="true"
      :modal="true"
      class="history-modal"
    >
      <template #header>
        <div class="w-full text-left">
          <h2 class="text-2xl font-bold text-gray-800 mb-1">
            Roll History for {{ values?.rollNumber }}
          </h2>
          <div class="flex items-center gap-2 text-sm text-gray-600">
            <Icon icon="mdi:timeline-clock-outline" class="text-lg" />
            <span>Complete timeline of roll status changes and actions</span>
          </div>

          <div class="mt-2 flex items-center gap-4 text-sm">
            <div class="flex items-center gap-1">
              <StatusTag :status="getRollState(values?.rollState)" />
              <span class="text-gray-500">Current Status</span>
            </div>
            <div class="flex items-center gap-1 text-gray-500">
              <Icon icon="mdi:information-outline" class="mr-1" />
              Batch: {{ getBatchNumber() }}
            </div>
          </div>
        </div>
      </template>

      <div v-if="timelineLoading" class="flex justify-center items-center h-64">
        <div class="flex flex-col items-center gap-3">
          <i class="pi pi-spinner pi-spin text-3xl text-primary-500"></i>
          <span class="text-gray-600">Loading roll timeline...</span>
        </div>
      </div>

      <div v-else class="h-full overflow-auto pt-4">
        <div v-if="rollTimelineActions.length === 0" class="text-center text-gray-500 py-8">
          <Icon icon="mdi:timeline-clock-outline" class="text-4xl mb-4" />
          <p>No timeline data available for this roll.</p>
        </div>

        <Timeline v-else :value="rollTimelineActions" align="left">
          <template #marker="slotProps">
            <span
              class="flex w-8 h-8 items-center justify-center text-white rounded-full z-10 shadow-md"
              :style="{ backgroundColor: slotProps.item.color }"
            >
              <i :class="slotProps.item.icon" class="text-lg"></i>
            </span>
          </template>
          <template #content="slotProps">
            <div>
              <div class="flex items-center gap-2">
                <span class="font-bold">{{ slotProps.item.status }}</span>
              </div>
              <div class="text-sm text-gray-500 mt-1">{{ slotProps.item.date }}</div>

              <div
                v-if="slotProps.item.details && Object.keys(slotProps.item.details).length > 0"
                class="mt-3 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm"
              >
                <dl class="space-y-1">
                  <div
                    v-for="(value, key) in slotProps.item.details"
                    :key="key"
                    class="flex"
                  >
                    <dt class="w-36 shrink-0 font-medium text-gray-500">{{ key }}:</dt>
                    <dd class="text-gray-800">{{ value }}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </template>
        </Timeline>
      </div>

      <template #footer>
        <div class="flex justify-end gap-3">
          <Button
            label="Close"
            icon="pi pi-times"
            @click="closeTimelineModal"
            outlined
          />
        </div>
      </template>
    </Dialog>

    <RollsForm :type="formType" :values="values" :onSubmit="handleEditRoll" :setErrors="setErrors" />

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
      <div class="ml-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Batch</label>
        <RouterLink
          :to="`/batches/${batch?.id}`"
          class="block bg-gray-50 rounded-lg p-2 text-gray-600 font-medium hover:bg-gray-100 hover:underline transition-colors"
        >
          {{ getBatchNumber() }}
        </RouterLink>
      </div>

      <div class="ml-5">
        <label class="block text-sm font-medium text-gray-700 mb-1">Customer Packing List</label>
        <template v-if="customerPackingList.length > 0">
          <div class="flex flex-wrap items-center gap-1 text-gray-600">
            <template v-for="(list, index) in customerPackingList" :key="list.id">
              <RouterLink
                :to="`/packingList/${(list as PackingList).id}`"
                class="block bg-gray-50 rounded-lg p-2 text-gray-600 font-medium hover:bg-gray-100 hover:underline transition-colors"
                >
                {{ list.packingListId }}
              </RouterLink>
              <span v-if="index < customerPackingList.length - 1">&gt;</span>
            </template>
          </div>
        </template>
        <p v-else class="text-gray-500 italic bg-gray-50 rounded-lg p-2 h-[42px] flex items-center">
          No customer packing list available
        </p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
      <div class="ml-6">
        <label class="block text-sm font-medium text-gray-700 mb-1">Supplier Packing List</label>
        <template v-if="supplierPackingList.length > 0">
          <div class="flex flex-wrap items-center gap-1 text-gray-600">
            <template v-for="(list, index) in supplierPackingList" :key="list.id">
              <RouterLink
                :to="`/packingList/${list.id}`"
                class="block bg-gray-50 rounded-lg p-2 text-gray-600 font-medium hover:bg-gray-100 hover:underline transition-colors"
              >
                {{ list.packingListId }}
              </RouterLink>
              <span v-if="index < supplierPackingList.length - 1">&gt;</span>
            </template>
          </div>
        </template>
        <p v-else class="text-gray-500 italic bg-gray-50 rounded-lg p-2 h-[42px] flex items-center">
          No supplier packing list available
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.history-modal :deep(.p-dialog-content) {
  padding: 1rem 1.5rem;
  overflow: hidden;
}

.history-modal :deep(.p-dialog-header) {
  padding: 1.5rem 1.5rem 1rem 1.5rem;
}

.history-modal :deep(.p-dialog-footer) {
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}
</style>